import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Inventory from 'components/Inventory';
import { SignUp } from '../components/Signup';
import Onboarding from '../components/Onboarding';
import Subscriptions from '../components/Subscription/Subscriptions';
import ForgotPassword from '../components/ForgotPassword';
import { ProtectedRoute } from '../components/ProtectedRoute';
import ConfirmNewPassword from '../components/ConfirmNewPassword';
import { Login, Transaction, Profile } from '../components';
import Invoice from 'components/Invoice';
import InventoryEmails from 'components/InventoryEmails';
import SecondSubscription from 'components/Subscription/SecondSubscription';

const HomeRedirect = () => {
    const companyData = useSelector((state) => state.company.data);
    const hasPurchaseAccess = companyData?.is_purchase_reconciler;
    const hasInvoiceAccess = companyData?.is_invoice_reconciler;

    if (!hasPurchaseAccess && !hasInvoiceAccess) {
        return <Navigate replace to="/profile/personal-settings" />;
    }

    if (!hasPurchaseAccess && hasInvoiceAccess) {
        return <Navigate replace to="/invoices/payments" />;
    }

    if (hasPurchaseAccess && !hasInvoiceAccess) {
        return <Navigate replace to="/purchases/for-review" />;
    }

    return <Navigate replace to="/purchases/for-review" />;
};

export const PRIVATE_ROUTES = [
    {
        path: '/onboarding',
        element: (
            <ProtectedRoute route="onboarding">
                <Onboarding />
            </ProtectedRoute>
        ),
        label: 'onboarding',
    },
    {
        path: '/inventory-emails/inventory-tab',
        element: (
            <ProtectedRoute requirePurchaseReconciler={true}>
                <Inventory label="Inventory" />
            </ProtectedRoute>
        ),
        label: 'Inventory',
    },
    {
        path: '/',
        element: (
            <ProtectedRoute>
                <HomeRedirect />
            </ProtectedRoute>
        ),
    },
    {
        path: '/transactions/*',
        element: (
            <ProtectedRoute route="transactions" requirePurchaseReconciler={true}>
                <Transaction />
            </ProtectedRoute>
        ),
        label: 'Transactions',
        children: [
            {
                index: true,
                element: <Navigate replace to="import-bank-transactions" />,
            },
            {
                path: 'import-bank-transactions',
                element: <Transaction />,
            },
            {
                path: 'upload-transactions',
                element: <Transaction />,
            },
        ],
    },
    // Redirect old transaction routes to new structure
    {
        path: '/transaction',
        element: <Navigate replace to="/transactions/import-bank-transactions" />,
    },
    {
        path: '/import-bank-transactions',
        element: <Navigate replace to="/transactions/import-bank-transactions" />,
    },
    {
        path: '/upload-transactions',
        element: <Navigate replace to="/transactions/upload-transactions" />,
    },
    {
        path: '/purchases/*',
        element: (
            <ProtectedRoute route="inventory" requirePurchaseReconciler={true}>
                <Inventory />
            </ProtectedRoute>
        ),
        label: 'Purchases',
    },
    {
        path: '/inventory-emails/*',
        element: (
            <ProtectedRoute route="inventory" requirePurchaseReconciler={true}>
                <InventoryEmails />
            </ProtectedRoute>
        ),
        label: 'Inventory Emails',
    },

    {
        path: '/profile/:section',
        element: (
            <ProtectedRoute>
                <Profile />
            </ProtectedRoute>
        ),
        label: 'Profile',
    },
    {
        path: '/profile',
        element: (
            <ProtectedRoute>
                <Navigate replace to="/profile/personal-settings" />
            </ProtectedRoute>
        ),
        label: 'Profile Redirect',
    },
    {
        path: '/subscriptions',
        element: (
            <ProtectedRoute route="subscriptions">
                <Subscriptions />
            </ProtectedRoute>
        ),
        label: 'Subscriptions',
    },
    {
        path: '/subscription-part-two',
        element: (
            <ProtectedRoute route="subscription-part-two">
                <SecondSubscription />
            </ProtectedRoute>
        ),
        label: 'Subscriptions',
    },

    {
        path: '/invoices/*',
        element: (
            <ProtectedRoute route="invoice">
                <Invoice />
            </ProtectedRoute>
        ),
        label: 'Invoices',
    },
    {
        path: '*',
        element: <Navigate to="/purchases/for-review" replace />,
    },
];

export const PUBLIC_ROUTES = [
    {
        path: '/login',
        element: <Login />,
    },
    {
        path: '/signup',
        element: <SignUp />,
    },
    {
        path: '/forgot-password',
        element: <ForgotPassword />,
    },
    {
        path: '/password-reset/:uid/:token',
        element: <ConfirmNewPassword />,
    },
];

export const PUBLIC_PATHNAMES = [
    'login',
    'signup',
    'forgot-password',
    'password-reset',
    'preview-invoice',
];
